import { Observer, Observable, Subject } from "rxjs";
import * as signalR from "@microsoft/signalr";
import {
    TaskModel,
    TaskCommentModel,
    ActionCompletedResult,
    ActionTriggeredResult,
    TaskError,
    MetadataPushMessage,
} from "../types";
import { Notification } from "../../common/types/Notification";
import { PackageChanges } from "../../common/services/types";
import {
    AssignmentMsg,
    UpdatedActivitiesPushMsg,
} from "../../home/types/Activity";
import { endpoints } from "../../../config";

// Helper function to get the push URL dynamically
const getPushUrl = () => {
    return process.env.NODE_ENV === "test" ? "test" : endpoints.pushUrl;
};

export class TaskPushClient {
    static RECONNECT_INTERVAL: number = 5000;
    static ConnectionAttempted: boolean = false;
    static ConnectionStatusSubject: Subject<boolean> = new Subject<boolean>();
    private static _connection: signalR.HubConnection | null = null;

    // Lazy getter for connection
    static get Connection(): signalR.HubConnection {
        if (!TaskPushClient._connection) {
            TaskPushClient._connection = new signalR.HubConnectionBuilder()
                .withUrl(getPushUrl())
                .build();

            // Set up reconnection handler
            TaskPushClient._connection.onclose(() => {
                TaskPushClient.connect();
            });
        }
        return TaskPushClient._connection;
    }

    constructor() {
        // Don't access Connection in constructor to avoid initialization race
        // The connection will be established when needed
    }

    private createListener<T>(
        methodName: string,
        logMessage: string,
    ): Observable<T> {
        return Observable.create((obs: Observer<T>) => {
            if (process.env.NODE_ENV === "test") {
                return;
            }

            const handler = (data: string) => {
                console.info(logMessage);
                obs.next(JSON.parse(data));
            };

            TaskPushClient.Connection.on(methodName, handler);

            return () => {
                TaskPushClient.Connection.off(methodName, handler);
            };
        });
    }

    static connect() {
        TaskPushClient.ConnectionAttempted = true;

        TaskPushClient.Connection.start()
            .then(() => {
                TaskPushClient.ConnectionStatusSubject.next(true);
            })
            .catch(() => {
                TaskPushClient.ConnectionStatusSubject.next(false);
                console.info("Wasn't able to connect to SignalR service");
                setTimeout(() => {
                    this.connect();
                }, TaskPushClient.RECONNECT_INTERVAL);
            });
    }

    createTaskListener(): Observable<TaskModel> {
        return this.createListener<TaskModel>(
            "TaskMessage",
            "Received data from push service on tasks...",
        );
    }

    updateTaskListener(): Observable<TaskModel> {
        return this.createListener<TaskModel>(
            "TaskFieldUpdateMessage",
            "Received data from push service on tasks...",
        );
    }

    createTaskNotificationsListener(): Observable<Notification> {
        return this.createListener<Notification>(
            "NotificationMessage",
            "Received data from push service on task notifications...",
        );
    }

    createTaskNewCommentListener(): Observable<
        TaskCommentModel & { projectId: string }
    > {
        return this.createListener<TaskCommentModel & { projectId: string }>(
            "TaskCommentMessage",
            "Received data from push service on task comments...",
        );
    }

    createTaskDeleteCommentListener(): Observable<string> {
        return this.createListener<string>(
            "TaskCommentDeleteMessage",
            "Received data from push service on task comments...",
        );
    }

    createTaskCommentDeleteListener(): Observable<string> {
        return this.createListener<string>(
            "TaskCommentDeleteMessage",
            "Received data from push service on task comments deletion...",
        );
    }

    createPackageChangeListener(): Observable<PackageChanges> {
        return this.createListener<PackageChanges>(
            "PackageStatusChange",
            "Received data from push service on package status change...",
        );
    }

    createActionCompletedListener(): Observable<ActionCompletedResult> {
        return this.createListener<ActionCompletedResult>(
            "ActionCompletedMessage",
            "Received data from push service on action completion...",
        );
    }

    createActionTriggeredListener(): Observable<ActionTriggeredResult> {
        return this.createListener<ActionTriggeredResult>(
            "ActionTriggeredMessage",
            "Received data from push service on action triggering...",
        );
    }

    createDashboardActivitiesListener(): Observable<UpdatedActivitiesPushMsg> {
        return this.createListener<UpdatedActivitiesPushMsg>(
            "DashboardActivitiesUpdate",
            "Received data from push service on task comments...",
        );
    }

    createTaskAssignmentListener(): Observable<AssignmentMsg> {
        return this.createListener<AssignmentMsg>(
            "TaskAssignment",
            "Received data from push service on task comments...",
        );
    }

    createNewTaskErrorListener(): Observable<TaskError> {
        return this.createListener<TaskError>(
            "TaskErrorMessage",
            "Received data from push service on task error...",
        );
    }

    createMedataUpdateListener(): Observable<MetadataPushMessage> {
        return this.createListener<MetadataPushMessage>(
            "MetadataUpdateMessage",
            "Received data from push service on task metadata...",
        );
    }
}

// Lazy initialization to avoid module loading order issues
let _tasksPushClient: TaskPushClient | null = null;

const getTasksPushClient = () => {
    if (!_tasksPushClient) {
        _tasksPushClient = new TaskPushClient();

        // Auto-connect only after the instance is created
        if (
            process.env.NODE_ENV !== "test" &&
            !TaskPushClient.ConnectionAttempted
        ) {
            TaskPushClient.connect();
        }
    }
    return _tasksPushClient;
};

export default {
    get pushClient() {
        return getTasksPushClient();
    },
    PushClient: TaskPushClient,
};
